import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

const positions = ['Goalkeeper', 'Right Back', 'Left Back', 'Centre Back', 'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder', 'Right Winger', 'Left Winger', 'Striker']

export default function Profile({ session }) {
  const uid = session.user.id
  const [profile, setProfile] = useState({ full_name: '', position: '', nationality: '', date_of_birth: '' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [saved, setSaved] = useState(false)
  const fileRef = useRef()

  useEffect(() => {
    supabase.from('profiles').select('*').eq('id', uid).single().then(({ data }) => {
      if (data) setProfile(data)
      setLoading(false)
    })
  }, [])

  const save = async () => {
    setSaving(true)
    await supabase.from('profiles').upsert({ ...profile, id: uid })
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const uploadAvatar = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `${uid}/avatar.${ext}`
    await supabase.storage.from('avatars').upload(path, file, { upsert: true })
    const { data } = supabase.storage.from('avatars').getPublicUrl(path)
    await supabase.from('profiles').upsert({ id: uid, avatar_url: data.publicUrl })
    setProfile(p => ({ ...p, avatar_url: data.publicUrl }))
    setUploading(false)
  }

  if (loading) return <div style={{ color: 'var(--muted)' }}>Loading...</div>

  return (
    <div>
      <div className="page-title mb-8">Profile</div>
      <div className="page-sub">Your personal details</div>

      <div style={{ maxWidth: 600 }}>
        {/* Avatar */}
        <div className="card mb-24">
          <div className="card-title">Profile Photo</div>
          <div className="flex gap-24" style={{ alignItems: 'center' }}>
            <div
              onClick={() => fileRef.current.click()}
              style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--dark3)', border: '2px solid var(--border)', overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: 28, color: 'var(--muted)' }}>◉</span>
              )}
            </div>
            <div>
              <button className="btn btn-ghost" onClick={() => fileRef.current.click()} disabled={uploading} style={{ fontSize: 13 }}>
                {uploading ? 'Uploading...' : 'Upload Photo'}
              </button>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>JPG or PNG. Click your photo to change it.</div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadAvatar} />
          </div>
        </div>

        {/* Details */}
        <div className="card mb-24">
          <div className="card-title">Personal Details</div>
          <div className="mb-16"><label>Full Name</label><input value={profile.full_name || ''} onChange={e => setProfile(p => ({...p, full_name: e.target.value}))} placeholder="Your full name" /></div>
          <div className="grid-2 mb-16">
            <div>
              <label>Position</label>
              <select value={profile.position || ''} onChange={e => setProfile(p => ({...p, position: e.target.value}))}>
                <option value="">Select position</option>
                {positions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
              </select>
            </div>
            <div><label>Nationality</label><input value={profile.nationality || ''} onChange={e => setProfile(p => ({...p, nationality: e.target.value}))} placeholder="e.g. British" /></div>
          </div>
          <div className="mb-24">
            <label>Date of Birth</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 8 }}>
              <select
                value={profile.date_of_birth ? profile.date_of_birth.split('-')[2] : ''}
                onChange={e => {
                  const parts = (profile.date_of_birth || '--').split('-')
                  setProfile(p => ({...p, date_of_birth: `${parts[0] || ''}-${parts[1] || ''}-${e.target.value}`}))
                }}
              >
                <option value="">DD</option>
                {Array.from({length: 31}, (_, i) => i + 1).map(d => (
                  <option key={d} value={String(d).padStart(2,'0')}>{String(d).padStart(2,'0')}</option>
                ))}
              </select>
              <select
                value={profile.date_of_birth ? profile.date_of_birth.split('-')[1] : ''}
                onChange={e => {
                  const parts = (profile.date_of_birth || '--').split('-')
                  setProfile(p => ({...p, date_of_birth: `${parts[0] || ''}-${e.target.value}-${parts[2] || ''}`}))
                }}
              >
                <option value="">Month</option>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                  <option key={m} value={String(i+1).padStart(2,'0')}>{m}</option>
                ))}
              </select>
              <select
                value={profile.date_of_birth ? profile.date_of_birth.split('-')[0] : ''}
                onChange={e => {
                  const parts = (profile.date_of_birth || '--').split('-')
                  setProfile(p => ({...p, date_of_birth: `${e.target.value}-${parts[1] || ''}-${parts[2] || ''}`}))
                }}
              >
                <option value="">YYYY</option>
                {Array.from({length: 40}, (_, i) => new Date().getFullYear() - 15 - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ minWidth: 120 }}>
            {saving ? <><span className="btn-spinner" /> Saving...</> : saved ? '✓ Saved' : 'Save Changes'}
          </button>
        </div>

        {/* Account */}
        <div className="card">
          <div className="card-title">Account</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 16 }}>Signed in as <strong style={{ color: 'var(--text)' }}>{session.user.email}</strong></div>
          <button
            className="btn btn-danger"
            onClick={async () => { await supabase.auth.signOut() }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  )
}
